/**
 * `<caos-breadcrumb>` — the ancestor trail back to a parent list or object.
 * (Catalog: Navigation › Breadcrumb — the trail back up from where you are.)
 *
 * Crumbs come from the `items` property (`{ label, href? }[]`) OR declarative
 * `<a href>` / `<span>` children in light DOM. The trail renders as a semantic
 * `<nav aria-label="Breadcrumb"><ol>`; the last crumb is the current page
 * (`aria-current="page"`, not a link). Chevron separators are drawn between crumbs
 * and hidden from assistive tech. Every colour reads a `--caos-*` token; links carry
 * the accent on hover. Styling seams: `::part(nav)`, `::part(list)`, `::part(item)`,
 * `::part(link)`, `::part(current)`, `::part(separator)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { makeIcon } from './icons.js';

export interface CaosBreadcrumbItem {
  label: string;
  href?: string;
}

const sheet = makeSheet(`
  :host { display: block; }

  nav { min-width: 0; }

  ol {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--caos-space-1);
    margin: 0;
    padding: 0;
    list-style: none;
  }

  li { display: inline-flex; align-items: center; gap: var(--caos-space-1); min-width: 0; }

  .crumb {
    font-family: var(--caos-font);
    font-size: 13px;
    line-height: 1.4;
    color: var(--caos-color-text-muted);
    text-decoration: none;
    border-radius: var(--caos-radius-sm);
    max-width: 22ch;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  a.crumb { cursor: pointer; }
  a.crumb:hover { color: var(--caos-color-accent); text-decoration: underline; }
  a.crumb:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  .current {
    color: var(--caos-color-text);
    font-weight: 600;
  }

  .sep {
    flex: none;
    width: 14px;
    height: 14px;
    color: var(--caos-color-text-muted);
    opacity: 0.7;
  }
  .sep svg { display: block; width: 100%; height: 100%; }
`);

/**
 * CAOS-602 — the separator between steps. It was drawn here because the vocabulary carried only a
 * DOWN chevron, and a trail needs one pointing the way it reads.
 */
const chevron = (): SVGSVGElement => makeIcon('chevron-right');

export class CaosBreadcrumb extends CaosElement {
  private nav: HTMLElement;
  private list: HTMLOListElement;
  private _items: CaosBreadcrumbItem[] | null = null;

  constructor() {
    super(sheet);
    this.nav = document.createElement('nav');
    this.nav.setAttribute('part', 'nav');
    this.nav.setAttribute('aria-label', 'Breadcrumb');
    this.list = document.createElement('ol');
    this.list.setAttribute('part', 'list');
    this.nav.append(this.list);
    this.root.append(this.nav);
  }

  connectedCallback(): void {
    if (this._items === null) this.readLightDom();
    this.render();
  }

  /** The trail (data in). Setting this rebuilds the crumbs. */
  get items(): CaosBreadcrumbItem[] {
    return this._items ?? [];
  }
  set items(list: CaosBreadcrumbItem[]) {
    this._items = list;
    this.render();
  }

  /** Read declarative `<a href>` / `<span>` children as the crumb source. */
  private readLightDom(): void {
    const nodes = Array.from(this.children).filter(
      (c) => c.tagName === 'A' || c.tagName === 'SPAN',
    );
    if (nodes.length) {
      this._items = nodes.map((n) => {
        const href = n.tagName === 'A' ? (n as HTMLAnchorElement).getAttribute('href') ?? undefined : undefined;
        return { label: (n.textContent ?? '').trim(), href };
      });
    }
  }

  private render(): void {
    const items = this._items ?? [];
    this.list.replaceChildren();
    items.forEach((item, i) => {
      const isLast = i === items.length - 1;
      const li = document.createElement('li');
      li.setAttribute('part', 'item');

      let crumb: HTMLElement;
      if (item.href && !isLast) {
        const a = document.createElement('a');
        a.href = item.href;
        a.setAttribute('part', 'link');
        crumb = a;
      } else {
        crumb = document.createElement('span');
        crumb.setAttribute('part', isLast ? 'current' : 'link');
      }
      crumb.classList.add('crumb');
      if (isLast) {
        crumb.classList.add('current');
        crumb.setAttribute('aria-current', 'page');
      }
      crumb.textContent = item.label;
      li.append(crumb);

      if (!isLast) {
        const sep = document.createElement('span');
        sep.className = 'sep';
        sep.setAttribute('part', 'separator');
        sep.setAttribute('aria-hidden', 'true');
        sep.append(chevron());
        li.append(sep);
      }
      this.list.append(li);
    });
  }
}

define('caos-breadcrumb', CaosBreadcrumb);

declare global {
  interface HTMLElementTagNameMap {
    'caos-breadcrumb': CaosBreadcrumb;
  }
}
